import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Check, Sparkles, Zap, Crown, ArrowRight, Rocket } from "lucide-react";

const ICONS = { free: Rocket, starter: Sparkles, pro: Zap, agency: Crown };

export default function Pricing() {
  const { t, lang, setLang } = useI18n();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(7);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/plans`).then((r) => { setPlans(r.data.plans); setTrialDays(r.data.trial_days); });
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#09090B]">
      <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 klein-blue flex items-center justify-center font-bold text-white" style={{borderRadius: 2}}>R</div>
            <span className="font-display font-bold text-lg">RecruitFlow <span className="text-[#002FA7]">AI</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="text-sm border border-zinc-200 px-2 py-1 bg-white uppercase" style={{borderRadius: 2}}>
              <option value="fr">FR</option><option value="en">EN</option><option value="hu">HU</option><option value="it">IT</option><option value="de">DE</option>
            </select>
            <Link to="/login" className="text-sm font-medium text-zinc-700 hover:text-black">{t("login")}</Link>
            <Link to="/register" className="btn-primary text-sm" style={{borderRadius: 2}}>{t("get_started")}</Link>
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-2xl mb-14 fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("pricing")}</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight mb-4">{t("pricing_headline")}</h1>
          <p className="text-lg text-zinc-600">{t("pricing_sub").replace("{days}", trialDays)}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 fade-up fade-up-2">
          {plans.map((p) => {
            const Icon = ICONS[p.id] || Sparkles;
            const featured = p.id === "pro";
            return (
              <div key={p.id} className={`relative p-8 ${featured ? "bg-[#09090B] text-white" : "swiss-card"}`} style={{borderRadius: 2}} data-testid={`pub-plan-${p.id}`}>
                {featured && <span className="absolute -top-3 left-6 bg-[#E63946] text-white text-[10px] uppercase tracking-wider px-2 py-1" style={{borderRadius: 2}}>{t("popular")}</span>}
                <Icon size={24} className={featured ? "text-[#E63946] mb-5" : "text-[#002FA7] mb-5"}/>
                <div className="font-display text-3xl font-bold tracking-tight">{p.name}</div>
                <div className="flex items-baseline gap-1 mt-4 mb-6">
                  <span className="font-display text-6xl font-bold tracking-tight">€{p.price}</span>
                  <span className={`text-sm ${featured ? "text-zinc-400" : "text-zinc-500"}`}>/{t("month")}</span>
                </div>
                <ul className="space-y-2.5 mb-8">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="mt-0.5 shrink-0 text-[#10B981]"/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid={`pub-cta-${p.id}`}
                  className={`w-full py-3 font-medium inline-flex items-center justify-center gap-2 ${
                    featured ? "bg-white text-black hover:bg-zinc-200" : "klein-blue hover:bg-[#00227A]"
                  }`}
                  style={{borderRadius: 2}}
                >
                  {t("start_trial").replace("{days}", trialDays)} <ArrowRight size={14}/>
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-sm text-zinc-500">{t("no_card_trial")}</div>
      </section>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-zinc-500">
          <div>© 2026 RecruitFlow AI</div>
          <div className="uppercase tracking-[0.2em] text-xs">Made for Europe</div>
        </div>
      </footer>
    </div>
  );
}
